import {
    Button,
    EditIcon,
    IconButton,
    ManagedCheckbox,
    MultiCheckbox,
    Popover,
    Rectangle,
    SearchInput,
    showToast,
    Skeleton,
} from '@lokalise/louis';
import { useMemo, useState } from 'react';

import { type Project } from '@app/api/styleGuide';
import { randomNumberFromRange } from '@app/utils/number';

import { useStyleGuideContext } from '../../StyleGuideContext';
import {
    AssignButton,
    AssignButtonContainer,
    MenuContainer,
    MenuListContainer,
    NoResults,
    SearchContainer,
} from '../../styles';
import OverflowTextTooltip from '../OverflowTextTooltip/OverflowTextTooltip';
import { AnimatedMenuItem, MenuFooterContainer, MenuItem, VerticalDivider } from './styles';

type ProjectsSelectorProps = {
    assignedProjects: Project[];
    styleGuideId: string;
};

const ProjectsSelector = ({ assignedProjects, styleGuideId }: ProjectsSelectorProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const { projects, saveStyleGuides } = useStyleGuideContext();

    const selectedProjectsIds = assignedProjects.map(({ id }) => id);

    const projectsKeyIndex = useMemo(
        () =>
            projects.reduce(
                (acc, curr) => ({
                    ...acc,
                    [curr.id]: curr,
                }),
                {} as { [id: string]: Project },
            ),
        [projects],
    );

    const filteredProjects = useMemo(
        () => projects.filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase())),
        [projects, searchValue],
    );

    const editStyleGuideProjects = async (projectIds: string[]) => {
        setIsLoading(true);
        const response = await saveStyleGuides([{ id: styleGuideId, projects: projectIds }]);

        if ('error' in response) {
            showToast({ type: 'error', title: 'Error while saving project. Please try again.' });
        }

        setIsLoading(false);
    };

    const onProjectSelect = async (projectIds: readonly string[]) => {
        const newSelectedProjects = projectIds.map((id) => projectsKeyIndex[id]);
        const newSelectedProjectsIds = newSelectedProjects.map(({ id }) => id);

        await editStyleGuideProjects(newSelectedProjectsIds);
    };

    const onSelectAll = () => editStyleGuideProjects(filteredProjects.map(({ id }) => id));

    const onClearAll = () => editStyleGuideProjects([]);

    return (
        <Popover
            placement="bottom-start"
            role="menu"
            content={
                <MenuContainer>
                    <SearchContainer>
                        <SearchInput
                            autoFocus
                            disabled={isLoading}
                            value={searchValue}
                            debounceTime={0}
                            placeholder="Search projects"
                            onValueChange={(value) => setSearchValue(value)}
                        />
                    </SearchContainer>

                    <MenuListContainer>
                        {filteredProjects.map(({ id, name }) => (
                            <MultiCheckbox
                                key={id}
                                name="selectedProjects"
                                onChange={(_, value) => onProjectSelect(value)}
                                value={selectedProjectsIds}
                            >
                                <MenuItem key={id} disabled={isLoading}>
                                    {isLoading ? (
                                        <AnimatedMenuItem>
                                            <Skeleton animated description="Project loading">
                                                <Rectangle key={id} height={14} width={14} />
                                            </Skeleton>
                                            <Skeleton animated description="Project loading">
                                                <Rectangle
                                                    key={id}
                                                    height={20}
                                                    width={`${randomNumberFromRange(40, 80)}%`}
                                                />
                                            </Skeleton>
                                        </AnimatedMenuItem>
                                    ) : (
                                        <ManagedCheckbox checked={selectedProjectsIds.includes(id)} value={id}>
                                            {name}
                                        </ManagedCheckbox>
                                    )}
                                </MenuItem>
                            </MultiCheckbox>
                        ))}
                        {filteredProjects.length === 0 && (
                            <NoResults>
                                {searchValue ? (
                                    <p>
                                        No match found for <strong>{searchValue}</strong>
                                    </p>
                                ) : (
                                    <p>No projects</p>
                                )}
                            </NoResults>
                        )}
                    </MenuListContainer>
                    <MenuFooterContainer>
                        <Button variant="tertiary" onClick={() => onSelectAll()} disabled={isLoading}>
                            Select all
                        </Button>
                        <VerticalDivider />
                        <Button variant="tertiary" onClick={() => onClearAll()} disabled={isLoading}>
                            Clear all
                        </Button>
                    </MenuFooterContainer>
                </MenuContainer>
            }
        >
            {assignedProjects.length !== 0 ? (
                <AssignButtonContainer>
                    <OverflowTextTooltip>
                        {assignedProjects.map((project) => project.name).join(', ')}
                    </OverflowTextTooltip>
                    <IconButton appearance="secondary" ariaLabel="Edit projects">
                        <EditIcon />
                    </IconButton>
                </AssignButtonContainer>
            ) : (
                <AssignButton variant="tertiary">Assign projects</AssignButton>
            )}
        </Popover>
    );
};

export default ProjectsSelector;
